import { Button, Input, Select } from "@/components";
import { Modal } from "@/components/modal/Modal";
import { useAuth } from "@/contexts/AuthContext";
import { PriorityRole, RoleEnum } from "@/types";
import isEmpty from "lodash/isEmpty";
import { Controller } from "react-hook-form";
import { useCreateUser } from "../hooks";
import { CreateUserProps } from "../types";
import { ROLE_LABELS } from "@/utils";

export function CreateUser({
  storeId,
  companyId,
  companyName,
  disabled,
}: CreateUserProps) {
  const {
    handleOpen,
    handleClose,
    open,
    handleSubmit,
    onSubmit,
    control,
    register,
    errors,
    isUser,
    handleChangeRoleField,
    isLoading,
  } = useCreateUser({ storeId, companyId, companyName });
  const { role } = useAuth();
  const ROLE_OPTIONS_CREATE_USER = [
    {
      ...(PriorityRole[role || RoleEnum.OTHER] >
        PriorityRole[RoleEnum.STORE_MANAGER] && {
        label: ROLE_LABELS.store,
        value: RoleEnum.STORE_MANAGER,
      }),
    },
    { label: ROLE_LABELS.user, value: RoleEnum.USER },
  ].filter((item) => !isEmpty(item));

  return (
    <>
      <Button
        buttonType="primary"
        className="bg-cyan !rounded-lg"
        onClick={handleOpen}
        disabled={disabled}
      >
        新規作成
      </Button>

      <Modal title="ユーザー作成" open={open} onOpenChange={handleOpen}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 p-4">
            {/* Role */}
            <Controller
              control={control}
              name="role"
              render={({ field: { onChange, value } }) => {
                const selectedRole = value
                  ? ROLE_OPTIONS_CREATE_USER.find((i) => i.value === value) ||
                    ROLE_OPTIONS_CREATE_USER[1]
                  : ROLE_OPTIONS_CREATE_USER[1];
                return (
                  <Select
                    label="役割"
                    value={selectedRole}
                    onChange={(item) => {
                      onChange(item?.value);
                      handleChangeRoleField();
                    }}
                    options={ROLE_OPTIONS_CREATE_USER}
                  />
                );
              }}
            />

            {/* User name */}
            <Input
              label="ユーザー名"
              placeholder="ユーザー名を入力してください。"
              required
              {...register("full_name")}
              error={errors.full_name?.message}
            />

            {/* Comapny name */}
            <Input
              label="営業所"
              placeholder="営業所を入力してください。"
              {...register("company_name")}
              error={errors.company_name?.message}
              hidden={isUser}
              disabled
            />

            {/* Position & Department */}
            <div className="grid grid-cols-2 gap-4" hidden={isUser}>
              <Input
                label="役職"
                placeholder="役職を入力してください。"
                {...register("position")}
                error={errors.position?.message}
                hidden={isUser}
              />
              <Input
                label="部署"
                placeholder="部署を入力してください。"
                {...register("department")}
                error={errors.department?.message}
                hidden={isUser}
              />
            </div>

            {/* Email */}
            <input
              type="email"
              tabIndex={-1}
              style={{
                position: "absolute",
                left: "-9999px",
                opacity: 0,
                pointerEvents: "none",
              }}
              autoComplete="email webauthn"
            />
            <Input
              label="メールアドレス"
              placeholder="メールアドレスを入力してください。"
              required
              {...register("email")}
              error={errors.email?.message}
              autoComplete="off"
            />

            {/* Password */}
            <input
              tabIndex={-1}
              style={{
                position: "absolute",
                left: "-9999px",
                opacity: 0,
                pointerEvents: "none",
              }}
              autoComplete="password"
            />
            <Input
              label="パスワード"
              placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
              required
              {...register("password")}
              error={errors.password?.message}
              autoComplete="off"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 p-4 border-t border-grey-300">
            <Button
              buttonType="text"
              className="!h-12 hover:bg-white !px-0 !rounded-lg !text-[15px] !font-bold"
              type="button"
              onClick={handleClose}
            >
              キャンセル
            </Button>
            <Button
              buttonType="primary"
              className="!h-12 px-[22px] py-[11px] !rounded-lg !text-[15px] !font-bold"
              type="submit"
              disabled={!isEmpty(errors) || isLoading}
            >
              作成
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
